<template>
  <a-config-provider>
    <div id="app">
      <router-view />
    </div>
  </a-config-provider>
</template>

<script>
import { computed } from 'vue'

/* ============================================================
 *  主题配色方案 — 加新主题只需在此对象中添加 key 即可
 *  使用方式: var t = THEMES.dark; t.layers.deep / t.vars.cardBg
 * ============================================================ */
/* 主题列表（供 layout.vue 下拉菜单使用） */
var THEME_LIST = [
  { key: 'light',   label: '亮色模式',   icon: 'sun' },
  { key: 'dark',    label: '暗色模式',   icon: 'moon' },
  { key: 'blue',    label: '星际蓝',     icon: 'cloud' },
  { key: 'cyber',   label: '科技风',     icon: 'thunderbolt' },
  { key: 'orange',  label: '星宇橙',     icon: 'fire' },
  { key: 'green',   label: '护眼绿',     icon: 'heart' },
  { key: 'purple',  label: '紫罗兰',     icon: 'star' },
]

var THEMES = {

  /* ---------- 亮色主题（默认） ---------- */
  light: {
    name: 'light', attr: '',
    bodyBg: '#f2f3f5', bodyText: '#1d2129',
    vars: {
      cardBg:'#fff', menuBg:'#fff', menuText:'#1d2129',
      menuSelBg:'rgba(0,0,0,.12)', menuSelTxt:'#165DFF',
      borderColor:'#e5e6eb', borderLight:'#e5e6eb',
      pageBg:'#f2f3f5', navbarBg:'#fff',
      textColor:'#1d2129', textSecond:'#86909c',
    },
    /* 左侧白 / 右侧浅灰 → 层次分明 */
    layers: { deep:'#f2f3f5', mid:'#ffffff', card:'#ffffff' },
    navbar:'#ffffff', aside:'#ffffff', modalBg:'#fff',
    hoverBg:'rgba(0,0,0,.08)', rowHover:'rgba(22,93,255,.04)',
  },

  /* ---------- 暗色主题 ---------- */
  dark: {
    name: 'dark', attr: 'dark',
    bodyBg: '#1a1a1a', bodyText: '#c9cdd4',
    vars: {
      cardBg:     '#262626',
      // menuBg:     '#232323',  /* 使用 theme.aside 作为回退值 */
      menuText:   '#e5e6eb',
      menuSelBg:  'rgba(255,255,255,.50)',
      menuSelTxt: '#165DFF',
      borderColor: '#363636',
      borderLight:'#363636',
      pageBg:     '#1a1a1a',
      navbarBg:   '#232323',
      textColor:  '#c9cdd4',
      textSecond: '#86909c',
    },
    layers: { deep:'#1a1a1a', mid:'#262626', card:'#303030' },
    navbar:'#232323', aside:'#232323', modalBg:'#2e2e2e',
    hoverBg:'rgba(255,255,255,.35)', rowHover:'rgba(22,93,255,.07)',
  },

  /* ================================================================
   *  以下每个主题都遵循四区域层次：
   *  navbar(顶栏) / aside(侧栏) / content-area(deep深底) / page-wrapper(mid灰层)
   *  四个颜色各不相同 → 层次分明
   * ================================================================ */

  /* ---------- 星际蓝 — 深空宇宙风 ---------- */
  blue: {
    name: 'blue', attr: 'blue',
    bodyBg: '#eef0fa',
    bodyText: '#1a2744',
    vars: {
      cardBg:     '#ffffff',
      // menuBg:     '#223d7c',
      menuText:   '#dbe4ff',
      menuSelBg:  'rgba(59,130,246,.70)',
      menuSelTxt: '#ffffff',
      borderColor: '#a8b8dc',
      borderLight:'#c5cee8',
      pageBg:     '#eef0fa',
      navbarBg:   '#1e3a8a',
      textColor:  '#1a2744',
      textSecond: '#3d5a94',
    },
    /* 四区域: 星云深蓝顶 / 宇宙蓝侧 / 银河灰蓝底 / 白卡片 */
    layers: { deep:'#eef0fa', mid:'#f4f6fc', card:'#ffffff' },
    navbar:'#1e3a8a', aside:'#223d7c', modalBg:'#f0f3fb',
    hoverBg:'rgba(255,255,255,.12)', rowHover:'rgba(59,130,246,.08)',
    navbarDark:true, asideDark:true,
  },

  /* ---------- 科技风 — 深青赛博风 ---------- */
  cyber: {
    name: 'cyber', attr: 'cyber',
    bodyBg: '#122545',
    bodyText: '#80c4ff',
    vars: {
      cardBg:     '#111f35',
      // menuBg:     '#0b182b',
      menuText:   '#5aadf0',
      menuSelBg:  'rgba(255,255,255,.20)',
      menuSelTxt: '#00d2ff',
      borderColor: '#1a3658',
      borderLight:'#1a3658',
      pageBg:     '#122545',
      navbarBg:   '#0c1829',
      textColor:  '#90cdff',
      textSecond: '#4890cc',
    },
    /* 四区域: 深青顶 / 更深侧 / 墨蓝深底 / 青蓝mid */
    layers: { deep:'#122545', mid:'#132a47', card:'#1a3858' },
    navbar:'#0c1829', aside:'#0b182b', modalBg:'#172e4d',
    hoverBg:'rgba(255,255,255,.15)', rowHover:'rgba(0,210,255,.08)',
  },

  /* ---------- 星宇橙 — 暖橘活力风 ---------- */
  orange: {
    name: 'orange', attr: 'orange',
    bodyBg: '#ffdab3',
    bodyText: '#552800',
    vars: {
      cardBg:     '#fff8f0',
      // menuBg:     '#ffc896',
      menuText:   '#5c3000',
      menuSelBg:  'rgba(255,255,255,.50)',
      menuSelTxt: '#E67000',
      borderColor: '#ebb078',
      borderLight:'#ebb078',
      pageBg:     '#ffdab3',
      navbarBg:   '#ffeee0',
      textColor:  '#552800',
      textSecond: '#804d20',
    },
    /* 四区域: 米白顶 / 橙侧 / 杏色深底 / 暖白mid */
    layers: { deep:'#ffdab3', mid:'#ffe5ce', card:'#fffaf3' },
    navbar:'#ffeee0', aside:'#ffc896', modalBg:'#fff3e5',
    hoverBg:'rgba(255,255,255,.35)', rowHover:'rgba(255,149,0,.08)',
  },

  /* ---------- 护眼绿 — 豆沙绿风 ---------- */
  green: {
    name: 'green', attr: 'green',
    bodyBg: '#cde0cb',
    bodyText: '#1a3320',
    vars: {
      cardBg:     '#f0f7ef',
      // menuBg:     '#b8d2b4',
      menuText:   '#1e4028',
      menuSelBg:  'rgba(255,255,255,.50)',
      menuSelTxt: '#228C44',
      borderColor: '#a3c49e',
      borderLight:'#a3c49e',
      pageBg:     '#cde0cb',
      navbarBg:   '#e2eede',
      textColor:  '#1a3320',
      textSecond: '#3a6040',
    },
    /* 四区域: 浅绿顶 / 绿侧 / 灰绿深底 / 薄绿mid */
    layers: { deep:'#cde0cb', mid:'#ddecdc', card:'#f0f7ef' },
    navbar:'#e2eede', aside:'#b8d2b4', modalBg:'#e2ede2',
    hoverBg:'rgba(255,255,255,.35)', rowHover:'rgba(34,150,68,.08)',
  },

  /* ---------- 紫罗兰 — 优雅紫风 ---------- */
  purple: {
    name: 'purple', attr: 'purple',
    bodyBg: '#dfd0ea',
    bodyText: '#281444',
    vars: {
      cardBg:     '#f6effa',
      // menuBg:     '#cbb8df',
      menuText:   '#301854',
      menuSelBg:  'rgba(255,255,255,.50)',
      menuSelTxt: '#722ED1',
      borderColor: '#b89fce',
      borderLight:'#b89fce',
      pageBg:     '#dfd0ea',
      navbarBg:   '#ece2f4',
      textColor:  '#281444',
      textSecond: '#54387a',
    },
    /* 四区域: 浅紫顶 / 紫侧 / 淡紫深底 / 薰衣草mid */
    layers: { deep:'#dfd0ea', mid:'#e8dff0', card:'#f6effa' },
    navbar:'#ece2f4', aside:'#cbb8df', modalBg:'#ede3f5',
    hoverBg:'rgba(255,255,255,.35)', rowHover:'rgba(114,46,220,.07)',
  },
}

/* 根据主题配置生成注入的 CSS 规则数组 */
function buildThemeCSS(theme) {
  var v = theme.vars
  var L = theme.layers
  /* light 主题 attr 为空 → 用 :not([arco-theme]) 匹配无属性状态 */
  var sel = theme.attr ? ('body[arco-theme="' + theme.attr + '"]') : 'body:not([arco-theme])'
  return [
    /* === CSS 变量 + body 基础 === */
    sel + '{'
      + '--card-bg:' + v.cardBg + ';'
      // + '--menu-bg:' + (v.menuBg || theme.aside) + ';'
      + '--menu-text:' + v.menuText + ';'
      + '--menu-selected-bg:' + v.menuSelBg + ';'
      + '--menu-selected-text:' + v.menuSelTxt + ';'
      + '--hover-bg:' + theme.hoverBg + ';'
      + '--row-hover:' + theme.rowHover + ';'
      + '--border-color:' + v.borderColor + ';'
      + '--border-color-light:' + v.borderLight + ';'
      + '--page-bg:' + v.pageBg + ';'
      + '--navbar-bg:' + v.navbarBg + ';'
      + '--text-color:' + v.textColor + ';'
      + '--text-secondary:' + v.textSecond + ';'
      + 'background-color:' + theme.bodyBg + '!important;'
      + 'color:' + theme.bodyText + '!important;'
    + '}',

    /* === 层级布局 (flex 自动撑满) === */
    sel + ' #app,' + sel + ' .layout-container,' + sel + ' .main-layout{background:' + L.deep + '!important}',
    sel + ' .content-area{background:' + L.deep + '!important;display:flex!important;flex-direction:column!important}',
    /* content-inner 透明 → 面包屑/标签栏区域露出浅灰底色 */
    sel + ' .content-inner{background:transparent!important;flex:1!important;display:flex!important;flex-direction:column!important}',
    /* 只有 page-wrapper 卡片区是白/mid 色 */
    // sel + ' .page-wrapper{background:' + L.mid + '!important;box-shadow:none!important;flex:1!important}',

    /* === 导航栏 === */
    sel + ' .navbar{background:' + theme.navbar + '!important;border-bottom-color:var(--border-color)!important}',
    /* 深色顶栏时：内部文字/图标/操作按钮强制浅色 */
    (theme.navbarDark ? [
      sel + ' .navbar .logo-text,' + sel + ' .navbar .top-tab,' + sel + ' .navbar .action-btn,'
      + sel + ' .navbar .icon-refresh,' + sel + ' .navbar .icon-fullscreen,' + sel + ' .navbar .icon-fullscreen-exit,'
      + sel + ' .navbar .icon-notification,' + sel + ' .navbar .user-name,' + sel + ' .navbar .icon-down{color:rgba(255,255,255,.88)!important}',
      sel + ' .navbar .top-tab:hover{color:#fff!important;font-weight:600!important;background:rgba(255,255,255,.12)!important}',
      sel + ' .navbar .top-tab.active{border-bottom-color:transparent!important;background:#fff!important;color:#165DFF!important;font-weight:700!important;border-radius:6px 6px 0 0!important;box-shadow:0 -2px 8px rgba(0,0,0,.15)!important}',
      sel + ' .navbar .action-btn:hover{background:rgba(255,255,255,.15)!important;color:#fff!important}',
      sel + ' .navbar .msg-dot{background:#ff4d4f!important;color:#fff!important}',
    ] : []),

    /* === 侧边栏/菜单 === */
    sel + ' aside{background:' + theme.aside + '!important;color:var(--menu-text)!important}',
    sel + ' [class*=arco-menu]{background:transparent!important;color:var(--menu-text)!important}',
    sel + ' .arco-menu-item:hover{background:var(--hover-bg)!important}',
    sel + ' aside .arco-menu .arco-menu-item.arco-menu-selected{background:var(--menu-selected-bg)!important;color:var(--menu-selected-text)!important;font-weight:600!important;border-radius:6px!important;}',
    /* 深色侧栏：强制覆盖子菜单父级inline style（图标+文字） */
    (theme.navbarDark || theme.asideDark ? [
      sel + ' aside .arco-menu-submenu-title span[style*="color"],'
      + sel + ' aside .arco-menu-popheader span[style*="color"],'
      + sel + ' aside .arco-menu-submenu-title .arco-icon[style*="color"],'
      + sel + ' aside .arco-menu-popheader .arco-icon[style*="color"]{color:#fff!important}',
    ] : []),

    /* === FLayout / FPluginList 业务组件 === */
    sel + ' .f-layout-header,' + sel + ' .f-layout-content,' + sel + ' .f-layout-level2,'
    + sel + ' .head,' + sel + ' .subHead,' + sel + ' .subHeadtop,' + sel + ' .subHeadbom,'
    + sel + ' .contentmain>div,' + sel + ' .contentBody>div,' + sel + ' .minor>div,'
    + sel + ' .f-layout-footer,' + sel + ' .drawer-top,' + sel + ' .drawer-bottom,'
    + sel + ' .fan-curd-modal{background:var(--card-bg)!important;color:var(--text-color)!important;border-color:var(--border-color)!important;box-shadow:none!important}',
    sel + ' .f-plugin-list,' + sel + ' .f-plugin-list>div,' + sel + ' .fan-f-layout,' + sel + ' .fan-f-layout>div{background:' + L.deep + '!important}',
    sel + ' .f-plugin-list .head,' + sel + ' .f-plugin-list .subHead{background:var(--card-bg)!important}',
    sel + ' .menu-backdrop,' + sel + ' .menu-backdrop>div,' + sel + ' .menu-backdrop>div>div{background:var(--menu-bg)!important}',
    sel + ' .fan-f-layout .arco-layout,' + sel + ' .fan-f-layout .arco-layout-content,'
    + sel + ' .fan-f-layout .arco-layout-header,' + sel + ' .fan-f-layout .arco-layout-footer{background:transparent!important}',

    /* === fan 组件透明 === */
    sel + ' .zee-table{background:transparent!important}',
    sel + ' .contentBody{background:transparent!important}',
    sel + ' [class*="zee-"],' + sel + ' [class*="fan-"]{background:transparent!important}',

    /* === Workplace 页面 === */
    // sel + ' .container{background:' + L.mid + '!important}',
    sel + ' .multi-tabs-wrap{background:' + L.deep + '!important}',
    sel + ' .panel,' + sel + ' [class*=card],' + sel + ' .moduler-wrap,' + sel + ' .data-panel{background:' + L.card + '!important}',
    sel + ' .mainBody{background:' + L.deep + '!important}',

    /* === 文字颜色 === */
    sel + ' .logo-text,' + sel + ' .user-name,' + sel + ' .panel-name,' + sel + ' .panel-desc,'
    + sel + ' .drawer-header,' + sel + ' .drawer-label,' + sel + ' .drawer-section-title,'
    + sel + ' .msg-header,' + sel + ' .msg-title,' + sel + ' .breadcrumb-wrap{color:var(--text-color,#e5e6eb)!important;background:transparent!important}',
    /* 深色主题（cyber/dark）：补全 Arco 内置暗色模式未覆盖的组件 */
    (theme.attr === 'cyber' || theme.attr === 'dark' ? [
      /* 标题/面板文字 */
      sel + ' .panel-name,' + sel + ' .panel-desc,' + sel + ' .panel-row,'
      + sel + ' .drawer-header,' + sel + ' .drawer-label,' + sel + ' .drawer-section-title,'
      + sel + ' .msg-header,' + sel + ' .msg-title,' + sel + ' .breadcrumb-wrap,'
      + sel + ' h1,' + sel + ' h2,' + sel + ' h3,' + sel + ' h4,' + sel + ' .head-title,'
      + sel + ' .subHead span,' + sel + ' .subHeadtop span{color:#e5ecf4!important}',
      /* a-card 卡片 */
      sel + ' .arco-card-header-title{color:#e5ecf4!important}',
      sel + ' .arco-card{border-color:var(--border-color)!important;background:var(--card-bg)!important}',
      /* a-statistic 统计数字 */
      sel + ' .arco-statistic-title{color:#c9cdd4!important;font-weight:600!important}',
      sel + ' .arco-statistic-value{color:#fff!important}',
      /* 通用文字/标签 */
      sel + ' .arco-tag{color:#e5ecf4!important;border-color:rgba(255,255,255,.2)!important}',
      sel + ' p,' + sel + ' span:not(.statistic-prefix):not([class*="icon"]){color:inherit}',
    ] : []),
    /* 科技风(cyber)专属：下拉面板/消息弹窗深色背景 */
    (theme.attr === 'cyber' ? [
      sel + ' .arco-dropdown,' + sel + ' .arco-dropdown-popup,' + sel + ' .arco-dropdown-popup .arco-dropdown-option-list,'
      + sel + ' .arco-popover-popup,' + sel + ' .arco-popover-popup-content{background:#0d1b2a!important;border:1px solid #1a3658!important;box-shadow:0 6px 16px rgba(0,20,40,.6)!important;color:#90cdff!important}',
      sel + ' .user-panel,.message-panel{background:#0d1b2a!important;color:#90cdff!important}',
      sel + ' .user-panel .panel-name{color:#90cdff!important}',
      sel + ' .user-panel .panel-desc{color:#4890cc!important}',
      sel + ' .user-panel .panel-row{color:#80c4ff!important}',
      sel + ' .user-panel .panel-row:hover{background:rgba(0,210,255,.12)!important}',
      sel + ' .user-panel .panel-divider{background:#1a3658!important}',
      sel + ' .msg-item{border-bottom-color:#1a3658!important;background:#122545!important}',
      sel + ' .msg-item:hover{background:#172e4d!important}',
      sel + ' .msg-desc{color:#80c4ff!important}',
      sel + ' .msg-time{color:#3d7ab8!important}',
      sel + ' .msg-footer{border-top-color:#1a3658!important;background:#0d1b2a!important}',
    ] : []),
    /* 面包屑最后一级：强制使用主题文字色（覆盖 Arco 默认 disabled 灰色） */
    sel + ' .arco-breadcrumb-item:last-child{color:var(--text-color,#e5e6eb)!important}',
    sel + ' .top-tab{color:var(--text-secondary,#86909c)!important}',
    sel + ' .top-tab.active{color:#165DFF!important}',
    sel + ' .action-btn{color:var(--text-secondary,#8b949e)!important}',
    sel + ' .action-btn:hover{color:#165DFF!important;background:' + theme.hoverBg + '!important}',
    sel + ' .action-btn.other-btn{background-color:#186df6!important;color:#fff!important}',
    sel + ' .action-btn.other-btn:hover{background-color:#5185f5!important;color:#fff!important}',
    sel + ' .action-btn.other-btn .arco-icon{color:inherit!important}',
    sel + ' .user-trigger:hover{background:' + theme.hoverBg + '!important}',
    sel + ' .panel-row{color:var(--text-color,#c9cdd4)!important}',
    sel + ' .panel-row:hover{background:' + theme.rowHover + '!important;color:#165DFF!important}',
    sel + ' .panel-divider{background:var(--border-color)!important}',
    sel + ' .msg-desc{color:var(--text-secondary,#86909c)!important}',
    sel + ' .msg-time{color:#5d6067!important}',
    sel + ' .msg-footer{border-top-color:var(--border-color)!important}',
    sel + ' .msg-item:hover{background:rgba(255,255,255,.04)!important}',
    sel + ' .menus{border-color:var(--border-color)!important}',
    sel + ' .drawer-item{border-color:var(--border-color)!important}',

    /* === 弹窗组件 === */
    sel + ' .arco-dropdown-popup{background:' + L.card + '!important;border:1px solid var(--border-color)!important;box-shadow:0 4px 12px rgba(0,0,0,.3)!important}',
    sel + ' .arco-drawer-body{background:var(--card-bg)!important}',
    sel + ' .arco-drawer-header{background:var(--card-bg)!important;border-bottom-color:var(--border-color)!important}',
    sel + ' .arco-modal-header{background:' + theme.modalBg + '!important;border-bottom:1px solid var(--border-color)!important;}',
    sel + ' .arco-modal-body{background:' + theme.modalBg + '!important;color:var(--text-color)!important;}',
    sel + ' .arco-modal{background:' + theme.modalBg + '!important;color:var(--text-color)!important}',
    sel + ' .arco-modal-footer{background:' + theme.modalBg + '!important;border-top:1px solid var(--border-color)!important;padding:10px;}',

    /* === 表格组件 === */
    sel + ' .arco-table{color:var(--text-color)!important;background:transparent!important}',
    /* 表头：稍亮背景 */
    sel + ' .arco-table-th{background:' + theme.layers.mid + '!important;color:var(--text-secondary)!important;font-weight:500!important;border-color:var(--border-color)!important}',
    /* 表格行：透明底 + 边框 */
    sel + ' .arco-table-td{color:var(--text-color)!important;border-color:var(--border-color)!important;background:transparent!important}',
    /* 只去除外层容器粗边框，保留单元格细线 */
    sel + ' .arco-table{border:none!important}',
    sel + ' .arco-table-container{border-left:1px solid var(--border-color)!important;border-top:1px solid var(--border-color)!important}',
    /* 行悬停：主题高亮 */
    sel + ' .arco-table-tr:hover .arco-table-td{background:var(--row-hover)!important}',
    /* 斑马纹：用 mid 层的半透明 */
    sel + ' .arco-table-tr-striped .arco-table-td{background:' + theme.layers.mid + '!important}',
    sel + ' .arco-table-empty{color:var(--text-secondary)!important}',
    /* 表格操作按钮 */
    sel + ' .action-btn{color:var(--text-secondary)!important;border-color:var(--border-color)!important}',
    sel + ' .action-btn:hover{color:#165DFF!important;background:' + theme.hoverBg + '!important}',
    sel + ' .action-btn.delete-btn{background:rgba(245,63,63,.3)!important;color:#f53f3f!important}',
    sel + ' .action-btn.other-btn{background-color:#186df6!important;color:#fff!important}',
    sel + ' .action-btn.other-btn:hover{background-color:#5185f5!important;color:#fff!important}',
    sel + ' .action-btn.other-btn .arco-icon{color:inherit!important}',
    /* 分页 */
    sel + ' .arco-pagination-item,' + sel + ' .arco-pagination-total{color:var(--text-secondary)!important}',
    sel + ' .arco-pagination-item-active{border-color:rgb(var(--primary-6))!important;color:rgb(var(--primary-6))!important}',

    /* === 弹窗内表单/文字 === */
    sel + ' .arco-form-item-label{color:var(--text-secondary)!important}',
    sel + ' .arco-input-wrapper,' + sel + ' .arco-select-view,' + sel + ' .arco-picker,' + sel + ' .arco-textarea-inner,' + sel + ' .arco-input-number-input{background:' + L.deep + '!important;color:var(--text-color)!important;border-color:var(--border-color)!important}',
    /* select 固定宽度（只对表单内生效，不覆盖搜索栏的 inline 宽度）width:100%!important;*/
    sel + ' .aform .arco-select-view{box-sizing:border-box!important}',
    sel + ' .aform .arco-select-view-single{display:flex!important;align-items:center!important}',
    sel + ' .aform .arco-select-view-value{flex:1!important;min-width:0!important;overflow:hidden!important;text-overflow:ellipsis!important}',
    /* select 内部元素统一使用 deep 背景（不透明）*/
    sel + ' .arco-select-view-single,.arco-select-view-multiple{background:' + L.deep + '!important;color:var(--text-color)!important}',
    /* select 下拉弹出层 */
    sel + ' .arco-select-popup{background:' + L.mid + '!important;border:1px solid var(--border-color)!important}',
    sel + ' .arco-select-option{color:var(--text-color)!important;background:transparent!important}',
    sel + ' .arco-select-option:hover{background:' + theme.hoverBg + '!important}',
    sel + ' .arco-select-option-active,' + sel + ' .arco-select-option.arco-select-option-selected{background:' + theme.hoverBg + '!important;color:rgb(var(--primary-6))!important}',
    sel + ' .aform .arco-form-item-content input,' + sel + ' .aform .arco-form-item-content textarea{color:var(--text-color)!important;background:transparent!important}',
    sel + ' .arco-tag{border-color:' + L.card + '!important;color:var(--text-color)!important}',
    sel + ' .arco-descriptions-item-label{color:var(--text-secondary)!important}',
    sel + ' .arco-descriptions-item-value{color:var(--text-color)!important}',

    /* === FDetail 详情表格（核心优化）=== */
    sel + ' #detail{background:var(--card-bg)!important;border-color:var(--border-color)!important;color:var(--text-color)!important;box-shadow:none!important}',
    /* 标签列：稍深的背景 + 次要文字色 */
    sel + ' #detail td.tdHeader{color:var(--text-secondary)!important;background:' + theme.layers.deep + '!important;border-color:var(--border-color)!important;font-weight:500!important;width:120px!important;min-width:80px!important}',
    /* 值列：主文字色 + 卡片背景 */
    sel + ' #detail td:not(.tdHeader){color:var(--text-color)!important;border-color:var(--border-color)!important;background:var(--card-bg)!important}',
    sel + ' #detail img{background:#000!important}',
    /* 弹窗标题 */
    sel + ' .drawer-header,' + sel + ' .arco-drawer-title,' + sel + ' .arco-modal-title{color:var(--text-color)!important}',
    /* 弹窗底部操作栏 */
    sel + ' .arco-drawer-footer,' + sel + ' .arco-modal-footer{background:' + theme.layers.deep + '!important;border-color:var(--border-color)!important}',
    /* 弹窗内普通按钮 */
    sel + ' .arco-drawer-footer .arco-btn,' + sel + ' .arco-modal-footer .arco-btn{border-color:var(--border-color)!important;color:var(--text-color)!important;background:' + L.card + '!important}',
    sel + ' .arco-drawer-footer .arco-btn:hover,' + sel + ' .arco-modal-footer .arco-btn:hover{color:#165DFF!important;border-color:#165DFF!important}',
    sel + ' .arco-drawer-footer .arco-btn-primary,' + sel + ' .arco-modal-footer .arco-btn-primary{background:rgb(var(--primary-6))!important;border-color:rgb(var(--primary-6))!important;color:#fff!important}',
    /* 主按钮 hover：保持白字 + 略亮主色，避免被下方通用 .arco-btn:hover 染成蓝字(蓝底)导致文字消失 */
    sel + ' .arco-drawer-footer .arco-btn-primary:hover,' + sel + ' .arco-modal-footer .arco-btn-primary:hover{color:#fff!important;background:rgb(var(--primary-5))!important;border-color:rgb(var(--primary-5))!important}',

    /* === 打印样式：强制深色文字+白底（所有主题通用）=== */
    '@media print{' + sel + '{color:#1d2129!important;background:#fff!important}',
    sel + ' *, ' + sel + ' *::before, ' + sel + ' *::after{color:#1d2129!important;background:#fff!important;border-color:#e5e6eb!important}',
    sel + ' #detail{color:#1d2129!important;background:#fff!important;border-color:#e5e6eb!important}',
    sel + ' #detail td{color:#1d2129!important;background:#fff!important;border-color:#e5e6eb!important}',
    sel + ' #detail td.tdHeader{color:#4e5969!important;background:#f7f8fa!important}',
    sel + ' img{-webkit-print-color-adjust:exact;print-color-adjust:exact}',
    '.no-print,.action-btn-group,.toolbar-right,.table-toolbar,.drawer-bottom,.arco-drawer-footer,.arco-modal-footer,.arco-pagination{display:none!important}}',
  ]
}

export default {
  name: 'App',
  created: function () {
    /* 注入所有主题样式（包括 light） */
    Object.keys(THEMES).forEach(function (k) { this._injectThemeStyle(k) }.bind(this))
    var s = (function () { try { return JSON.parse(localStorage.getItem('layoutSettings') || '{}') } catch(e) { return {} } })()
    this._applyTheme(s.themeMode || 'light')
    if (s.colorWeak) document.body.classList.add('color-weak')
    if (s.grayscale) document.body.style.filter = 'grayscale(100%)'
  },
  methods: {
    /** 切换主题（设置 arco-theme 属性 + 背景色） */
    _applyTheme: function (mode) {
      var t = THEMES[mode] || THEMES.light
      var el = document.body
      if (t.attr) { el.setAttribute('arco-theme', t.attr) }
      else { el.removeAttribute('arco-theme') }
      el.style.backgroundColor = t.bodyBg
    },

    /** 注入指定主题的样式，支持多主题共存 */
    _injectThemeStyle: function (themeName) {
      var theme = THEMES[themeName]
      if (!theme) return
      var styleId = 'fan-theme-' + themeName
      var old = document.getElementById(styleId)
      if (old) old.remove()
      var rules = buildThemeCSS(theme)
      var el = document.createElement('style')
      el.id = styleId
      el.textContent = rules.join('\n')
      document.head.appendChild(el)
    },
  }
}
</script>

<style>
/* global reset */
* { margin: 0; padding: 0; box-sizing: border-box; }
html, body { width: 100%; height: 100%; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
#app { width: 100%; min-height: 100vh; }

/* 图表组件全局样式（参考 Pro） */
.general-card {
  border-radius: 4px;
  transition: box-shadow 0.2s;
}
.general-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.general-card .arco-card-header {
  padding: 16px 20px 12px;
}
.general-card .arco-card-header-title {
  font-size: 14px;
  font-weight: 500;
}
.general-card .arco-card-body {
  padding: 12px 20px 20px;
}
.statistic-prefix {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  margin-right: 10px;
  font-size: 18px;
}
.breadcrumb-wrap {
  padding: 4px 0;
}


/* 所有主题：菜单 hover + 选中高亮 */
body[arco-theme] .arco-menu-item:hover { background: var(--hover-bg, rgba(255,255,255,.35)) !important; }
body[arco-theme] .arco-menu-item.arco-menu-selected { 
  background: var(--menu-selected-bg, rgba(255,255,255,.50)) !important; 
  color: var(--menu-selected-text, #165DFF) !important;
}
/* 收缩状态下：菜单选中项显示背景色（非竖条） */
body[arco-theme] .arco-menu--collapse .arco-menu-item.arco-menu-selected {
  background: var(--menu-selected-bg, rgba(22,93,255,.25)) !important;
  border-radius: 6px !important;
  /* margin: 2px 6px !important; */
}
/* 隐藏收缩状态的竖条指示器 */
body[arco-theme] .arco-menu--collapse .arco-menu-indicator,
body[arco-theme] .arco-menu--collapse .arco-menu-item::before {
  display: none !important;
}
</style>

<!-- ====== cyber/dark 深色主题：组件文字强制白色（静态CSS，确保生效） ====== -->
<style>
body[arco-theme="cyber"] .arco-card-header-title,
body[arco-theme="dark"] .arco-card-header-title { color:#e5ecf4 !important }

body[arco-theme="cyber"] .arco-statistic-title,
body[arco-theme="dark"] .arco-statistic-title { color:#c9cdd4 !important; font-weight:600 !important }

body[arco-theme="cyber"] .arco-statistic-value,
body[arco-theme="dark"] .arco-statistic-value { color:#fff !important }

body[arco-theme="cyber"] .arco-card,
body[arco-theme="dark"] .arco-card { background:var(--card-bg) !important; border-color:var(--border-color) !important }

body[arco-theme="cyber"] h1,body[arco-theme="cyber"] h2,body[arco-theme="cyber"] h3,body[arco-theme="cyber"] h4,
body[arco-theme="dark"] h1,body[arco-theme="dark"] h2,body[arco-theme="dark"] h3,body[arco-theme="dark"] h4,
body[arco-theme="cyber"] .panel-name,body[arco-theme="cyber"] .panel-desc,
body[arco-theme="dark"] .panel-name,body[arco-theme="dark"] .panel-desc { color:#e5ecf4 !important }
</style>
